import { EntityIconRoundSm } from '@components/core-ui/EntityIcon/variations/EntityIconRoundSm';
import { OutlinedLink } from '@components/core-ui/form-elements/buttons/Button/variations/OutlinedButton';
import { ButtonMedium } from '@components/core-ui/form-elements/buttons/Button/variations/PrimaryButtonSizes';
import { VsLogo } from '@components/Logo';
import { useAuthContext } from '@providers/AuthProvider';
import { routes } from '@routes';
import { getOrganizationSlugFromPathname } from '@wrappers/routes/AuthenticatedRoutes/helpers/getOrganizationSlugFromPathname';
import { Helmet } from 'react-helmet-async';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { getHomeOrganizationSlug } from './helpers/getHomeOrganizationSlug';

const MAX_ORGANIZATION_LINKS = 6;

export const NotFoundPage = () => {
  const { me, organization } = useAuthContext();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const organizations = (me?.organizations ?? []).filter((item) => !item.deleted);
  const homeOrganizationSlug = getHomeOrganizationSlug(organization?.id, organizations);
  const requestedOrganizationSlug = getOrganizationSlugFromPathname(pathname);

  const goHome = () => {
    navigate(homeOrganizationSlug ? routes.dashboard.make(homeOrganizationSlug) : routes.organizationsList.make(), {
      replace: true,
    });
  };

  return (
    <>
      <Helmet>
        <title>Page not found - AutoScript</title>
      </Helmet>
      <div className="relative flex min-h-screen w-full flex-col items-center justify-center overflow-hidden bg-page px-4 py-12 text-center">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -left-24 top-1/4 h-72 w-72 rounded-full bg-[var(--color-tint)] opacity-70 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-24 bottom-1/4 h-80 w-80 rounded-full bg-[var(--color-accent-subtle)] opacity-40 blur-3xl"
        />

        <div className="relative flex w-full max-w-[560px] flex-col items-center gap-8">
          <VsLogo hideText />

          <main className="flex w-full flex-col items-center gap-6 rounded-2xl border border-border bg-surface px-6 py-10 shadow-popover sm:px-12">
            <p
              aria-hidden="true"
              className="select-none bg-gradient-to-r from-[var(--color-gradient-from)] to-[var(--color-gradient-to)] bg-clip-text text-[80px] font-semibold leading-none tracking-tight text-transparent sm:text-[104px]"
            >
              404
            </p>

            <div className="flex flex-col gap-3">
              <h1 className="text-[28px] font-semibold leading-9 text-title">Page not found</h1>
              <p className="text-base leading-6 text-foreground-muted">
                This page does not exist, or you are not a member of the organization it belongs to.
              </p>
            </div>

            {requestedOrganizationSlug ? (
              <p className="flex flex-wrap items-center justify-center gap-2 text-sm text-foreground-subtle">
                <span>Requested organization</span>
                <code className="rounded-md bg-surface-muted px-2 py-1 font-mono text-xs text-foreground">
                  {requestedOrganizationSlug}
                </code>
              </p>
            ) : null}

            <div className="flex flex-wrap items-center justify-center gap-3">
              <ButtonMedium onClick={goHome}>Go to Dashboard</ButtonMedium>
              {organizations.length > 0 ? (
                <OutlinedLink className="btn--md no-underline" to={routes.organizationsList.make()}>
                  View all organizations
                </OutlinedLink>
              ) : null}
            </div>
          </main>

          {organizations.length > 0 ? (
            <section aria-labelledby="not-found-organizations-heading" className="flex w-full flex-col gap-3">
              <h2
                id="not-found-organizations-heading"
                className="text-xs font-medium uppercase tracking-wide text-foreground-subtle"
              >
                Your organizations
              </h2>
              <ul className="flex flex-wrap justify-center gap-2">
                {organizations.slice(0, MAX_ORGANIZATION_LINKS).map((item) => (
                  <li key={item.id}>
                    <Link
                      to={routes.dashboard.make(item.slug)}
                      className="flex items-center gap-2 rounded-full border border-border bg-surface py-1 pl-1 pr-3.5 text-sm font-medium text-foreground no-underline transition-colors hover:border-link hover:text-link"
                    >
                      <span aria-hidden="true">
                        <EntityIconRoundSm
                          name={item.name}
                          src={item.image}
                          sizeClassName="h-7 w-7 bg-[var(--color-avatar-bg)] text-[10px] leading-none text-[var(--color-on-surface)]"
                        />
                      </span>
                      {item.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          <footer className="flex flex-col gap-1 text-xs leading-[130%] text-foreground-muted">
            <p>
              <a
                className="text-foreground-muted hover:text-link"
                href="https://voicescript.ai/privacy-policy/"
                target="_blank"
                rel="noreferrer"
              >
                Privacy Policy
              </a>{' '}
              |{' '}
              <a
                className="text-foreground-muted hover:text-link"
                href="https://voicescript.ai/terms-of-service/"
                target="_blank"
                rel="noreferrer"
              >
                Terms of Service
              </a>
            </p>
            <p>&copy; {new Date().getFullYear()} VoiceScript Inc. All Rights Reserved</p>
          </footer>
        </div>
      </div>
    </>
  );
};
